import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  title?: string;
  description?: string;
  icon?: string;
  tone?: 'default' | 'danger' | 'warning';
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const ICON_TONES = {
  default: 'bg-primary-100 text-primary-700',
  danger: 'bg-red-100 text-red-600',
  warning: 'bg-amber-100 text-amber-700',
};

export default function Modal({
  open,
  title,
  description,
  icon,
  tone = 'default',
  onClose,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/45 animate-fade-in"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={[
          'relative w-full rounded-lg border border-background-200 bg-background-50 p-5 animate-scale-in md:p-6',
          SIZES[size],
        ].join(' ')}
      >
        {(title || icon) && (
          <div className="mb-4 flex items-start gap-3">
            {icon && (
              <span
                className={[
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
                  ICON_TONES[tone],
                ].join(' ')}
              >
                <i className={`${icon} text-xl`}></i>
              </span>
            )}
            <div className="min-w-0 flex-1">
              {title && (
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {title}
                </h3>
              )}
              {description && (
                <p className="mt-1 text-sm text-foreground-600">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        )}
        {children}
        {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}